// Per-course final-quiz attempt history. Today: localStorage; later:
// quiz_attempts + a small server endpoint. Used by the final-quiz page for:
//   - 24h cooldown enforcement after a failed attempt (advisory only — see
//     replit.md migration notes; real enforcement waits for auth so it
//     can't be bypassed by clearing localStorage),
//   - smart-rotation history carried into the next attempt request.
//
// Storage shape (under  lo:final_quiz_history:<course_slug>):
//   {
//     attempts: [
//       {
//         ts: <ms epoch>,
//         passed: <bool>,
//         score: <int>, total: <int>, percent: <int>,
//       },
//       ...most recent first
//     ],
//     rotation_history: <opaque server-signed string | null>
//   }
//
// rotation_history is the OPAQUE server-signed blob returned by /score. The
// client treats it as a black box: it cannot decode, inspect, or modify it.
// On the next /attempt request the client echoes it back so the server can
// extract the seen / wrong sets and drive smart rotation. This replaces an
// earlier shape that stored raw question_ids / wrong_question_ids per
// attempt — that shape leaked per-question correctness via set-difference,
// which defeats the whole anti-gaming design of the final assessment.
//
// The attempts array stays bounded to MAX_HISTORY entries, but it now only
// holds the bits needed for cooldown / pass-status UX. None of the entries
// expose question identities.

const KEY_PREFIX = 'lo:final_quiz_history:';
const MAX_HISTORY = 20;
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours after a failed attempt

function storageKey(courseSlug) { return `${KEY_PREFIX}${courseSlug}`; }

function safeParse(raw) {
  if (!raw) return { attempts: [], rotation_history: null };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      const attempts = Array.isArray(parsed.attempts)
        ? parsed.attempts.filter((a) => a && Number.isFinite(a.ts))
        : [];
      const rotation_history =
        typeof parsed.rotation_history === 'string' && parsed.rotation_history.length > 0
          ? parsed.rotation_history
          : null;
      return { attempts, rotation_history };
    }
  } catch (_) { /* fall through */ }
  return { attempts: [], rotation_history: null };
}

function readState(courseSlug) {
  if (typeof window === 'undefined') return { attempts: [], rotation_history: null };
  try { return safeParse(window.localStorage.getItem(storageKey(courseSlug))); }
  catch (_) { return { attempts: [], rotation_history: null }; }
}

function writeState(courseSlug, state) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(courseSlug), JSON.stringify(state));
  } catch (_) { /* incognito with disabled storage — silent no-op */ }
}

// Record a scored attempt. We deliberately do NOT accept question_ids here
// any more — see the file header for why. Pass `rotation_history` (the
// opaque blob from /score) so the next /attempt can carry it back to the
// server.
export function recordAttempt(courseSlug, attempt) {
  const state = readState(courseSlug);
  const trimmed = {
    ts: Number.isFinite(attempt.ts) ? attempt.ts : Date.now(),
    passed: !!attempt.passed,
    score: Number.isFinite(attempt.score) ? attempt.score : 0,
    total: Number.isFinite(attempt.total) ? attempt.total : 0,
    percent: Number.isFinite(attempt.percent) ? attempt.percent : 0,
    breakdown: Array.isArray(attempt.breakdown) ? attempt.breakdown : [],
  };
  const newRotationHistory =
    typeof attempt.rotation_history === 'string' && attempt.rotation_history.length > 0
      ? attempt.rotation_history
      : state.rotation_history;
  writeState(courseSlug, {
    attempts: [trimmed, ...state.attempts].slice(0, MAX_HISTORY),
    rotation_history: newRotationHistory,
  });
}

export function getAttempts(courseSlug) {
  return readState(courseSlug).attempts;
}

export function getMostRecentAttempt(courseSlug) {
  return readState(courseSlug).attempts[0] || null;
}

// Returns the opaque server-signed rotation blob (or null). The client
// treats this as a black box — pass it to /attempt and let the server
// decide what's inside.
export function getRotationHistory(courseSlug) {
  return readState(courseSlug).rotation_history;
}

// Returns ms remaining on a cooldown, or 0 if not in cooldown. The cooldown
// applies ONLY after a FAILED attempt — passing the quiz unlocks the
// certificate and there's no reason to slow the learner down. The most-
// recent attempt is the one we check; an older failed attempt that's
// already > 24h old doesn't gate a fresh one.
//
// SECURITY NOTE: this is advisory until auth lands. A determined user can
// bypass the cooldown by clearing localStorage / using incognito. Real
// enforcement requires server-side per-user attempt history (see the
// migration plan in replit.md). The smart-rotation gate stays effective
// either way because it's signed server-side.
export function getCooldownRemainingMs(courseSlug) {
  const last = getMostRecentAttempt(courseSlug);
  if (!last) return 0;
  if (last.passed) return 0;
  const elapsed = Date.now() - last.ts;
  if (elapsed >= COOLDOWN_MS) return 0;
  return COOLDOWN_MS - elapsed;
}

// True if the learner has ever recorded a passing attempt for this course.
// Used to soft-gate the "Open my certificate" CTA on the Course Complete
// lesson — the cert page itself does NOT yet enforce this (it'd be trivially
// bypassed by clearing localStorage), so this is a UX honesty gate, not a
// security gate. Real enforcement waits for auth + server-side quiz_attempts
// inspection.
export function hasPassedFinal(courseSlug) {
  for (const a of readState(courseSlug).attempts) {
    if (a.passed) return true;
  }
  return false;
}

export function clearFinalQuizHistory(courseSlug) {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(storageKey(courseSlug)); } catch (_) {}
}

// Format an "X hours, Y minutes" string for the cooldown UI.
export function formatCooldown(ms) {
  if (ms <= 0) return '';
  const totalMin = Math.ceil(ms / 60000);
  if (totalMin < 60) return `${totalMin} minute${totalMin === 1 ? '' : 's'}`;
  const hr = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  if (min === 0) return `${hr} hour${hr === 1 ? '' : 's'}`;
  return `${hr}h ${min}m`;
}
